<template>
  <div class="ltr">
    <table class="table is-bordered" style="height:1px">
      <thead>
        <tr>
          <th class="has-text-centered" style="max-width:30px;">Status</th>
          <th class="has-text-centered">Category</th>
          <th class="has-text-centered">
            <div>Applicant</div>
            <div class="is-size-7 has-text-weight-normal">{{ applicant.first_name }} {{ applicant.last_name }}</div>
          </th>
          <th class="has-text-centered">
            <div>Position</div>
            <div class="is-size-7 has-text-weight-normal">{{ position.title }}</div>
            <div class="is-size-7 has-text-weight-normal" v-if="position.company">{{ position.company.name }}</div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr style="max-width:30px;">
          <td style="height: 100%;" class="p-0 is-flex is-flex-direction-column">
            <div v-for="(value, key) in calculateJobTitleStatus" :key="key" :class="value.class" style="width: 100%;" class="is-flex is-flex-grow-1 is-align-items-center is-justify-content-center">
              <div class="has-text-centered">
                <p class="is-size-7">{{ key }}</p>
                <p class="is-size-7">{{ value.text }}</p>
              </div>
            </div>
          </td>
          <td>job title</td>
          <td style="vertical-align: top;">
            <ApplicantJobTitles :applicant="applicant" @updateJobTitles="handleApplicantJobTitles" />
          </td>
          <td>
            <div class="box">
              <PositionJobTitles :position="position" @updateJobTitles="handlePositionJobTitles" />
            </div>
          </td>
        </tr>
        <tr>
          <td :class="calculateSalaryStatus"></td>
          <td>salary</td>
          <td>
            <SalaryExpectations field="min" v-model="applicant.min_salary_expectations" :applicantId="applicant.resource_id" @error="handleMinSalaryExpectationsError" />
            <ApplicantSalaryExpectationsComment v-model="applicant.salary_expectations_comment" :applicantId="applicant.resource_id" @error="handleMaxSalaryExpectationsError" />
          </td>
          <td>
            <MaxSalaryExpectations v-model="position.max_salary_expectations" :positionId="position.id" />
            <PositionSalaryExpectationsComment v-model="position.salary_expectations_comment" :positionId="position.id" />
          </td>
        </tr>
        <tr>
          <td :class="calculateDistanceStatus"></td>
          <td>distance</td>
          <td>
            <PreferredWorkRegion :applicantId="applicant.resource_id" v-model="applicant.preferred_work_region" />
            <LocationEditor
              :onSubmit="handleLocationApplicant"
              :payload="{
                location: applicantLocation,
                resource_type: 'Applicant',
                resource_id: applicant.resource_id
              }"
            />
          </td>
          <td style="vertical-align: bottom;">
            <LocationEditor
              :onSubmit="handleLocationPositions"
              :payload="{
                location: positionLocation,
                resource_type: 'Position',
                resource_id: position.id
              }"
            />
          </td>
        </tr>
        <tr>
          <td colspan="4" class="has-text-centered is-size-3 has-text-weight-bold">
            <span>Skills</span>
          </td>
        </tr>
        <tr v-for="skill in skills" :key="skill.id">
          <td>{{ skill.position.skill.name }}</td>
          <td>
            <span>
              {{ positionSkillGroup(skill.position) }}
            </span>
          </td>
          <td>
            <div class="columns is-align-items-center">
              <div class="column">
                <IsApproved v-if="skill.applicant" :applicantId="applicant.resource_id" :applicantSkill="skill.applicant" @updated="notifyParent"/>
              </div>
              <div class="column">
                <button
                  class="button is-fullwidth"
                  :class="{ 'is-success': skill.applicant && skill.applicant.details.has_experience === true }"
                  @click="answerSkillQuestion(skill.position, true)"
                >
                  Yes
                </button>
              </div>
              <div class="column">
                <button
                  class="button is-fullwidth"
                  :class="{ 'is-danger': skill.applicant && skill.applicant.details.has_experience === false }"
                  @click="answerSkillQuestion(skill.position, false)"
                >
                  No
                </button>
              </div>
            </div>
          </td>
          <td>
            <div class="dropdown" :class="{ 'is-active': isEditingComment === skill.id }">
              <div class="dropdown-trigger is-small"  @click="toggleCommentEdit(skill.id, skill.applicant ? skill.applicant.applicant_comment : '')">
                  <span v-if="skill.applicant">
                    <span>{{ skill.applicant.applicant_comment ? skill.applicant.applicant_comment : 'New comment ...' }}</span>
                  </span>
                  <span v-else>
                    <span>New comment ...</span>
                  </span>
              </div>
              <div class="dropdown-menu" role="menu">
                <div class="dropdown-content">
                  <div class="dropdown-item">
                    <textarea
                      v-model="currentComment"
                      class="textarea is-small"
                      rows="3"
                      style="width: 300px;"
                    ></textarea>
                    <div class="buttons my-2">
                      <button class="button is-small is-success" @click="saveComment(skill.applicant.id)">Save</button>
                      <button class="button is-small mx-2" @click="cancelEdit">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import SalaryExpectations from '@/components/entities/applicant/SalaryExpectations.vue'
import MaxSalaryExpectations from '@/components/entities/position/MaxSalaryExpectations.vue'
import ApplicantSalaryExpectationsComment from '@/components/entities/applicant/SalaryExpectationsComment.vue'
import PositionSalaryExpectationsComment from '@/components/entities/position/SalaryExpectationsComment.vue'
import PreferredWorkRegion from '@/components/entities/applicant/PreferredWorkRegion.vue'
import PositionJobTitles from '@/components/entities/position/JobTitles.vue'
import ApplicantJobTitles from '@/components/entities/applicant/JobTitles.vue'
import LocationEditor from '@/components/LocationEditor.vue'
import IsApproved from '@/components/entities/applicantSkill/IsApproved.vue'

export default {
  name: 'ModalEvaluate',
  components: {
    SalaryExpectations,
    MaxSalaryExpectations,
    ApplicantSalaryExpectationsComment,
    PositionSalaryExpectationsComment,
    PreferredWorkRegion,
    PositionJobTitles,
    ApplicantJobTitles,
    LocationEditor,
    IsApproved
  },
  props: {
    applicant: {
      type: Object,
      required: true,
      validator: value => !!value.resource_id
    },
    position: {
      type: Object,
      required: true,
      validator: value => !!value.id
    }
  },
  data () {
    return {
      applicantLocation: '',
      positionLocation: '',
      minSalaryExpectationsError: false,
      maxSalaryExpectationsError: false,
      skills: [],
      applicantJobTitles: [],
      positionJobTitles: [],
      isEditingComment: null,
      currentComment: ''
    }
  },
  computed: {
    calculateDistanceStatus () {
      const dist = this.calculateDistance()
      return dist <= 40 ? 'has-background-success' : 'has-background-warning'
    },
    calculateSalaryStatus () {
      const applicantSalary = parseFloat(this.applicant.min_salary_expectations)
      const positionSalary = parseFloat(this.position.max_salary_expectations)
      if (isNaN(applicantSalary) || isNaN(positionSalary) || this.minSalaryExpectationsError || this.maxSalaryExpectationsError) {
        return ''
      }
      return applicantSalary > positionSalary ? 'has-background-danger' : 'has-background-success'
    },
    calculateJobTitleStatus () {
      const jobTitlesStatus = {}

      for (let posJobTitle of this.positionJobTitles) {
        const posJobTitleId = posJobTitle.id
        const posJobTitleName = posJobTitle.name
        const posMinExperience = parseFloat(posJobTitle.min_experience) || 0
        const posMaxExperience = parseFloat(posJobTitle.max_experience) || 100

        const appJobTitle = this.applicantJobTitles.find(
          (appJT) => appJT.id === posJobTitleId
        )

        if (appJobTitle) {
          const appExperience = parseFloat(appJobTitle.started_at) || 0
          const appIsInterested = appJobTitle.is_interested

          if (appIsInterested) {
            if (
              appExperience >= posMinExperience &&
              appExperience <= posMaxExperience
            ) {
              jobTitlesStatus[posJobTitleName] = {
                icon: 'mark-check.svg',
                class: 'has-background-success'
              }
            } else {
              jobTitlesStatus[posJobTitleName] = {
                icon: 'mark-question.svg',
                text: '- Seniority mismatch',
                class: 'has-background-warning'
              }
            }
          } else {
            jobTitlesStatus[posJobTitleName] = {
              icon: 'mark-question.svg',
              text: '- Not interested',
              class: 'has-background-warning'
            }
          }
        } else if (this.applicant.applicant_status !== 'completed') {
          jobTitlesStatus[posJobTitleName] = {
            text: 'Need to do vetting for applicant'
          }
        } else {
          jobTitlesStatus[posJobTitleName] = {
            icon: 'mark-close.svg',
            text: 'X',
            class: 'has-background-danger'
          }
        }
      }

      return jobTitlesStatus
    }
  },
  async created () {
    await this.fetchLocations()
    this.skills = await this.getPositionSkillsWithApplicantMark()
  },
  methods: {
    handleMinSalaryExpectationsError (status) {
      this.minSalaryExpectationsError = status
    },
    handleMaxSalaryExpectationsError (status) {
      this.maxSalaryExpectationsError = status
    },
    handlePositionJobTitles (data) {
      this.positionJobTitles = data
    },
    handleApplicantJobTitles (data) {
      this.applicantJobTitles = data
    },
    async fetchLocations () {
      await Promise.all([
        this.getLocation('Applicant', this.applicant.resource_id),
        this.getLocation('Position', this.position.id)
      ])
    },
    calculateDistance (lat1, lon1, lat2, lon2) {
      // Convert latitude and longitude from degrees to radians
      const toRadians = (degrees) => (degrees * Math.PI) / 180

      lat1 = parseFloat(this.applicantLocation.lat)
      lon1 = parseFloat(this.applicantLocation.lng)
      lat2 = parseFloat(this.positionLocation.lat)
      lon2 = parseFloat(this.positionLocation.lng)

      const R = 6371 // Radius of the Earth in kilometers
      const dLat = toRadians(lat2 - lat1)
      const dLon = toRadians(lon2 - lon1)

      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) *
          Math.cos(toRadians(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2)

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

      return R * c // Distance in kilometers
    },
    positionSkillGroup (skill) {
      switch (skill.importance) {
        case 2:
          return 'Optional'
        case 8:
          return 'Mandatory'
        default:
          return `Group name: ${skill.group_name}`
      }
    },
    async getLocation (type, id) {
      try {
        const response = await this.$network_utils.getResourceLocations({
          current_type: type,
          current_resource_id: id
        })
        this[`${type.toLowerCase()}Location`] = response[0] || ''
      } catch (error) {
        console.error(`Failed to fetch location for ${type} with id ${id}`, error)
      }
    },
    handleLocationPositions (data) {
      this.positionLocation = data
    },
    handleLocationApplicant (data) {
      this.applicantLocation = data
    },
    async notifyParent () {
      this.skills = await this.getPositionSkillsWithApplicantMark()
    },
    async getPositionSkillsWithApplicantMark () {
      try {
        const applicantSkills = await this.$network_utils.getApplicantSkills({ current_applicant_id: this.applicant.resource_id })
        return this.position.positions_skills.map(positionSkill => {
          const applicantSkill = applicantSkills.find(applicantSkill => applicantSkill.skill.id === positionSkill.skill.id)
          return {
            id: positionSkill.id,
            position: positionSkill,
            applicant: applicantSkill || null
          }
        })
      } catch (error) {
        console.error('Failed to fetch position skills with applicant mark', error)
        return []
      }
    },
    async answerSkillQuestion (skill, answer) {
      try {
        const response = await this.$network_utils.postApplicantSkill({
          skill_id: skill.skill.id,
          current_applicant_id: this.applicant.resource_id,
          has_experience: answer
        })
        this.skills = this.skills.map(s => s.id === skill.id ? { ...s, applicant: { ...response, details: { ...response.details, has_experience: answer } } } : s)
      } catch (error) {
        console.error('Failed to answer skill question', error)
      }
    },
    toggleCommentEdit (skillId, comment) {
      if (this.isEditingComment === skillId) {
        this.isEditingComment = null
        this.currentComment = ''
      } else {
        this.isEditingComment = skillId
        this.currentComment = comment
      }
    },
    async saveComment (skillId) {
      try {
        const response = await this.$network_utils.updateApplicantSkill({
          id: skillId,
          current_applicant_id: this.applicant.resource_id,
          applicant_comment: this.currentComment
        })
        this.skills = this.skills.map(skill => {
          if (skill.applicant && skill.applicant.id === skillId) {
            return { ...skill, applicant: { ...skill.applicant, applicant_comment: response.applicant_comment } }
          } else {
            return skill
          }
        })
        this.isEditingComment = null
      } catch (error) {
        console.error('Failed to save comment', error)
      }
    },
    cancelEdit () {
      this.isEditingComment = null
      this.currentComment = ''
    }
  }
}
</script>
